import { useState, useCallback, useEffect } from 'react';
import { notify } from '../../../utils/toast';
import {
    connectMetaAccount,
    MetaOAuthRedirectPending,
    clearMetaOAuthSession,
} from '../../../services/metaService';
import {
    readMetaModal,
    stashMetaModal,
    clearMetaModalStash,
    hasPendingMetaOAuthCallback,
    META_MODAL_READY_EVENT,
} from '../../onboarding/metaReturn';

import { MetaModalData } from '../types/meta';

interface UseMetaAuthFlowReturn {
    isConnecting: boolean;
    authModalData: MetaModalData | null;
    startAuthFlow: (organizationId: string) => Promise<void>;
    checkPendingAuth: () => Promise<void>;
    clearAuthData: () => void;
}

export function useMetaAuthFlow(): UseMetaAuthFlowReturn {
    // Lazy init: se o OAuth terminou antes desta página montar, o modal já nasce aberto
    const [isConnecting, setIsConnecting] = useState(false);
    const [authModalData, setAuthModalData] = useState<MetaModalData | null>(() => readMetaModal());

    const finishWithModalData = useCallback((data: MetaModalData) => {
        stashMetaModal(data);
        clearMetaOAuthSession();
        setAuthModalData(data);
        queueMicrotask(() => {
            window.dispatchEvent(new CustomEvent(META_MODAL_READY_EVENT));
        });
    }, []);

    useEffect(() => {
        const hydrate = () => {
            const stashed = readMetaModal();
            if (stashed) setAuthModalData(stashed);
        };

        hydrate();
        window.addEventListener(META_MODAL_READY_EVENT, hydrate);
        return () => window.removeEventListener(META_MODAL_READY_EVENT, hydrate);
    }, []);

    const startAuthFlow = useCallback(async (organizationId: string) => {
        if (!organizationId) {
            notify.error('Organização não encontrada');
            return;
        }

        setIsConnecting(true);
        sessionStorage.setItem('adlitics_meta_connecting', 'true');

        let redirectingToMeta = false;
        try {
            const data = await connectMetaAccount(organizationId);
            finishWithModalData(data);
        } catch (error: unknown) {
            if (error instanceof MetaOAuthRedirectPending) {
                redirectingToMeta = true;
                return;
            }
            console.error('[useMetaAuthFlow] Erro ao conectar:', error);
            clearMetaOAuthSession();
            const message = error instanceof Error ? error.message : 'Erro ao conectar com Meta';
            notify.error(message);
        } finally {
            if (!redirectingToMeta) {
                setIsConnecting(false);
            }
        }
    }, [finishWithModalData]);

    const checkPendingAuth = useCallback(async () => {
        if (hasPendingMetaOAuthCallback()) return;
        if (readMetaModal()) return;

        if (sessionStorage.getItem('adlitics_meta_connecting') === 'true') {
            clearMetaOAuthSession();
            setIsConnecting(false);
        }
    }, []);

    const clearAuthData = useCallback(() => {
        setAuthModalData(null);
        clearMetaModalStash();
    }, []);

    return {
        isConnecting,
        authModalData,
        startAuthFlow,
        checkPendingAuth,
        clearAuthData
    };
}
